import { FunctionComponent, RefObject, useEffect, useRef, useState } from 'react';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import ChatHeader from './ChatHeader';
import {
    createEmptyProspectMessage,
    Meta,
    Prospect,
    ProspectConversationDetail,
    ProspectMessage
} from 'model/ProspectMessage';
import { fetchProspectConversation, saveProspectMessage } from 'actions/prospectMessage/prospectMessageActions';
import { ProspectMessageStatus } from 'reducers/prospectMessage/prospectMessageState';
import ChatMessages from './ChatMessages';
import ChatInput from '../../Chat/ChatInput';
import ChatIsEmpty from '../../Chat/ChatIsEmpty';
import { fetchTwoWaySmsCountThunk } from 'actions/twoWaySms/twoWaySmsActions';

export interface ChatProps {
    prospect: Prospect | undefined;
    marketplaceId: number;
    onSaveNewClient: () => void;
    setProspect: (prospect: Prospect | undefined) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
        },
        chatDisplayContainerNoHeader: {
            width: '100%',
            flexGrow: 1
        },
        chatDisplayContainer: {
            width: '100%',
            flexGrow: 1,
            maxWidth: '100%'
        },
        chatInputContainer: {
            width: '100%',
            zIndex: 99,
            borderTop: '#D4D4D4 1px solid'
        }
    })
);

const ProspectChat: FunctionComponent<ChatProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);

    const chatScrollRef = useRef<OverlayScrollbarsComponent>(null);
    const lastMessageRef = useRef<HTMLDivElement | null>(null);
    const lastVisibleMessageRef = useRef<string | null>(null);

    const timeZone = useSelector<RootState, string>(state => state.login.auth.timeZone!);
    const prospectConversationDetail = useSelector<RootState, ProspectConversationDetail | null>(
        state => state.prospectMessages.prospectConversationDetail
    );
    const prospectConversationStatus = useSelector<RootState, ProspectMessageStatus>(
        state => state.prospectMessages.status
    );
    const prospectConversationDetailMeta = useSelector<RootState, Meta | null | undefined>(
        state => state.prospectMessages.prospectConversationDetail?.meta
    );

    const [newMessage, setNewMessage] = useState('');
    const [newProspectMessage, setNewProspectMessage] = useState<ProspectMessage | undefined>(undefined);
    const [page, setPage] = useState(prospectConversationDetail?.meta.page || 1);
    const [pageSize, setPageSize] = useState(prospectConversationDetail?.meta.size || 30);

    useEffect(() => {
        if (props.prospect && props.prospect.id) {
            setNewProspectMessage(createEmptyProspectMessage(props.prospect.id));
        }
    }, [props.prospect]);

    useNonInitialEffect(() => {
        if (props.prospect && prospectConversationDetail) {
            setProspectMessagesAsRead(prospectConversationDetail?.messages || []);
        }
    }, [prospectConversationDetail]);

    useNonInitialEffect(() => {
        if (prospectConversationStatus === ProspectMessageStatus.FetchingConversationDetailSuccess) {
            setLoading(false);
            const shouldScroll = isLastMessageVisible();

            if (shouldScroll) {
                scrollToBottomHandler(chatScrollRef);
            } else {
                if (lastVisibleMessageRef.current) {
                    setTimeout(() => {
                        const target = document.getElementById(lastVisibleMessageRef.current!);
                        if (target) {
                            target.scrollIntoView({ block: 'start' });
                        }
                        lastVisibleMessageRef.current = null;
                    }, 50);
                }
            }
        }

        if (prospectConversationStatus === ProspectMessageStatus.SaveSuccess) {
            if (props.prospect && props.prospect.id) {
                setNewMessage('');
                setNewProspectMessage(createEmptyProspectMessage(props.prospect.id));
                scrollToBottomHandler(chatScrollRef);
            }
        }
    }, [prospectConversationStatus]);

    useEffect(() => {
        setLoading(true);
        if (props.prospect && props.prospect.id) {
            dispatch(fetchProspectConversation(props.prospect.id, page, pageSize, props.marketplaceId));
        }
    }, [props.prospect]);

    useNonInitialEffect(() => {
        setLoading(true);
        if (props.prospect && props.prospect.id) {
            dispatch(fetchProspectConversation(props.prospect.id, page, pageSize, props.marketplaceId));
        }
    }, [props.marketplaceId]);

    useNonInitialEffect(() => {
        scrollToBottomHandler(chatScrollRef);
    }, [loading]);

    const scrollToBottomHandler = (ref: RefObject<OverlayScrollbarsComponent>) => {
        setTimeout(() => {
            const instance = ref.current?.osInstance();
            if (instance) {
                const maxScrollTop =
                    instance.getElements().viewport.scrollHeight - instance.getElements().viewport.clientHeight;
                instance.scroll({ y: maxScrollTop }, 300);
            }
        }, 250);
    };

    const scrollHandler = () => {
        const instance = chatScrollRef.current?.osInstance();
        const scrollTop = instance?.scroll().position.y ?? 0;

        if (
            scrollTop < 50 &&
            !loading &&
            props.prospect &&
            prospectConversationDetailMeta &&
            prospectConversationDetailMeta.page !== undefined &&
            prospectConversationDetailMeta.totalPages !== undefined &&
            prospectConversationDetailMeta.page < prospectConversationDetailMeta.totalPages
        ) {
            const viewport = instance?.getElements().viewport;
            const messages = viewport?.querySelectorAll('[id^="msg-"]') || [];

            for (const el of Array.from(messages)) {
                const rect = (el as HTMLElement).getBoundingClientRect();
                if (rect.top >= 0 && rect.top <= window.innerHeight) {
                    lastVisibleMessageRef.current = el.id;
                    break;
                }
            }
            dispatch(
                fetchProspectConversation(
                    props.prospect.id,
                    prospectConversationDetailMeta?.page + 1,
                    pageSize,
                    props.marketplaceId
                )
            );
        }
    };

    const sendMessageHandler = () => {
        if (newProspectMessage) {
            const messageToSentOther = { ...newProspectMessage };
            if (props.marketplaceId && props.marketplaceId !== 0) {
                messageToSentOther.marketplaceId = props.marketplaceId;
                messageToSentOther.senderId = props.marketplaceId;
            }
            if (props.prospect && props.prospect.id) {
                messageToSentOther.prospectId = props.prospect.id;
                messageToSentOther.receiverId = props.prospect.id;
            }
            messageToSentOther.sentAt = new Date();
            messageToSentOther.readAt = new Date();

            setNewProspectMessage(messageToSentOther);
            dispatch(saveProspectMessage(messageToSentOther, props.marketplaceId));
        }
    };

    const changeMessageHandler = (text: string) => {
        if (newProspectMessage) {
            const messageToSentOther = { ...newProspectMessage };
            messageToSentOther.message = text;
            setNewProspectMessage(messageToSentOther);
            setNewMessage(text);
        }
    };

    const setProspectMessagesAsRead = (messages: Array<ProspectMessage>) => {
        const unReadMessages = messages.filter(message => !message.readAt && message.senderType === 'Prospect');
        if (unReadMessages && unReadMessages.length > 0) {
            for (const message of unReadMessages) {
                message.readAt = new Date();
                dispatch(saveProspectMessage(message, props.marketplaceId));
            }
            dispatch(fetchTwoWaySmsCountThunk(props.marketplaceId));
        }
    };

    const isLastMessageVisible = (): boolean => {
        const el = lastMessageRef.current;
        if (!el) return false;

        const rect = el.getBoundingClientRect();
        return rect.bottom <= window.innerHeight;
    };

    return (
        <Box className={classes.root}>
            {props.prospect ? (
                <>
                    <ChatHeader
                        prospect={props.prospect}
                        marketplaceId={props.marketplaceId}
                        onSaveNewClient={props.onSaveNewClient}
                        onBackButton={() => props.setProspect(undefined)}
                    />
                    <Box
                        className={props.prospect ? classes.chatDisplayContainer : classes.chatDisplayContainerNoHeader}
                    >
                        <ChatMessages
                            prospect={props.prospect}
                            loading={loading}
                            messages={prospectConversationDetail?.messages || []}
                            timeZone={timeZone}
                            chatScrollRef={chatScrollRef}
                            lastMessageRef={lastMessageRef}
                            onScroll={scrollHandler}
                        />
                    </Box>
                    <Box className={classes.chatInputContainer}>
                        <ChatInput
                            isActive={!!props.prospect}
                            messageText={newMessage}
                            onMessageChange={changeMessageHandler}
                            onSendMessage={sendMessageHandler}
                        />
                    </Box>
                </>
            ) : (
                <ChatIsEmpty />
            )}
        </Box>
    );
};

export default ProspectChat;
